import React, { useCallback, useEffect, useState, useMemo, useRef } from "react";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../@/components/ui/tabs";
import { EmptyState } from "@/components/emptyState";
import { BookOpenIcon } from "lucide-react";
import { Skeleton } from "../../@/components/ui/skeleton";
import ReviewedCard from "@/components/reviewedCard";
import UnreviewedCard from "@/components/unreviewedCard";
import axios from "axios";
import { api_endpoint } from "@/constants/constant";
import { useAuth, useUser } from "@clerk/clerk-react";
import TableToolbar from "../components/tableToolbar";
import { useSelector } from "react-redux";
import { fetchAllPaginatedItems } from "@/lib/fetchAllPaginatedItems";
import { useSidebarPageTitle } from "@/shared/useSidebarPageTitle";
import Pagination from "../components/pagination";

function UnreviewedCardsSkeleton({ rows = 6 } = {}) {
  return (
    <div className="space-y-4">
      {Array.from({ length: rows }).map((_, idx) => (
        <div
          key={idx}
          className="w-full rounded-lg border border-(--neutral-300) bg-(--surface-base) p-6"
        >
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0 flex-1 space-y-3">
              <Skeleton className="h-5 w-2/3" />
              <Skeleton className="h-4 w-11/12 max-w-176" />
            </div>
            <div className="flex flex-col items-end gap-2">
              <Skeleton className="h-10 w-28 rounded-md" />
              <Skeleton className="h-10 w-12 rounded-md" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function ReviewedCardsSkeleton({ rows = 6 } = {}) {
  return (
    <div className="space-y-6">
      {Array.from({ length: rows }).map((_, idx) => (
        <div
          key={idx}
          className="bg-(--surface-base) border-2 border-(--neutral-300) rounded-lg overflow-hidden m-0"
        >
          <div className="py-2 px-4 border-b border-(--neutral-300)">
            <div className="flex flex-row justify-between items-start flex-wrap gap-3">
              <div className="flex flex-col gap-2 min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <Skeleton className="h-5 w-2/3 max-w-160" />
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Skeleton className="h-5 w-28 rounded-full" />
                  <Skeleton className="h-4 w-80 max-w-full" />
                </div>
              </div>

              <div className="flex flex-col items-end min-w-20 gap-2">
                <Skeleton className="h-10 w-24" />
                <Skeleton className="h-6 w-20 rounded-full" />
              </div>
            </div>
          </div>

          <div className="px-4 sm:px-6 pt-6 pb-0">
            <div className="mb-6 space-y-2">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-11/12" />
              <Skeleton className="h-4 w-10/12" />
              <Skeleton className="h-4 w-9/12" />
            </div>

            <div className="border-t border-(--neutral-300) bg-(--neutral-100) -mx-6 px-6 py-3">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 w-full">
                <Skeleton className="h-4 w-80 max-w-full" />
                <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                  <Skeleton className="h-9 w-28" />
                  <Skeleton className="h-9 w-28" />
                </div>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function parseProjectsResponse(payload) {
  // Expected shapes (varies by endpoint):
  // - { projects: { data: [], pagination: {...} } }
  // - { projects: [], pagination: {...} }
  // - { items: [], pagination: {...} }
  // - { data: [], pagination: {...} }
  // - []
  const root = payload?.projects ?? payload;

  const pagination =
    root?.pagination ?? payload?.pagination ?? payload?.projects?.pagination ?? null;

  if (Array.isArray(root)) {
    return { data: root, pagination };
  }

  if (Array.isArray(root?.data)) {
    return { data: root.data, pagination: root?.pagination ?? pagination };
  }

  if (Array.isArray(payload?.projects)) {
    return { data: payload.projects, pagination };
  }
  if (Array.isArray(payload?.items)) {
    return { data: payload.items, pagination };
  }
  if (Array.isArray(payload?.data)) {
    return { data: payload.data, pagination };
  }
  if (Array.isArray(payload?.results)) {
    return { data: payload.results, pagination };
  }

  return { data: [], pagination };
}

function normalizePaginationMeta(pagination, fallback = {}) {
  const current_page = Number(pagination?.current_page ?? fallback.current_page ?? 1) || 1;
  const page_size = Number(pagination?.page_size ?? fallback.page_size ?? 10) || 10;
  const total_count = Number(pagination?.total_count ?? fallback.total_count ?? 0) || 0;
  const total_pages = Number(pagination?.total_pages ?? fallback.total_pages ?? 1) || 1;
  const has_next = Boolean(pagination?.has_next ?? (current_page < total_pages));
  const has_previous = Boolean(pagination?.has_previous ?? (current_page > 1));
  return {
    current_page,
    page_size,
    total_count,
    total_pages,
    has_next,
    has_previous,
  };
}

export default function ReviewProjects() {
  const sidebarTitle = useSidebarPageTitle();
  const [activeTab, setActiveTab] = useState("unreviewed");
  const TAB_STORAGE_KEY = "zindua_reviewprojects_active_tab";
  const [shouldRenderUnreviewedTab, setShouldRenderUnreviewedTab] = useState(true);
  const [shouldRenderReviewedTab, setShouldRenderReviewedTab] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("submissionDate");
  const [filterBy, setFilterBy] = useState("all");
  const [reviewedProjects, setReviewedProjects] = useState([]);
  const [unreviewedProjects, setUnreviewedProjects] = useState([]);
  const [unreviewedLoading, setUnreviewedLoading] = useState(false);
  const [reviewedLoading, setReviewedLoading] = useState(false);
  const [error, setError] = useState("");
  const [courses, setCourses] = useState([]);
  const [projectTypeFilter, setProjectTypeFilter] = useState("all");
  const { user, isLoaded } = useUser();
  const { getToken, userId } = useAuth();
  const roles = useSelector((s) => s.auth.roles) || [];
  const isAdmin = roles.includes("Admin");
  const isManager = roles.includes("Manager");
  const isPrivileged = isAdmin || isManager;

  const readModeFlags = () => {
    if (typeof window === "undefined") return { adminMode: false, managerMode: false };
    return {
      adminMode: window.localStorage.getItem("zindua_admin_mode") === "on",
      managerMode: window.localStorage.getItem("zindua_manager_mode") === "on",
    };
  };

  const [modeFlags, setModeFlags] = useState(() => readModeFlags());
  useEffect(() => {
    const handle = () => setModeFlags(readModeFlags());
    window.addEventListener("zindua-mode-changed", handle);
    return () => window.removeEventListener("zindua-mode-changed", handle);
  }, []);

  const isPrivilegedModeOn = useMemo(() => {
    if (!isPrivileged) return false;
    if (isAdmin) return Boolean(modeFlags.adminMode);
    if (isManager) return Boolean(modeFlags.managerMode);
    return false;
  }, [isPrivileged, isAdmin, isManager, modeFlags.adminMode, modeFlags.managerMode]);

  // Privileged-mode Instructor filter (mirrors Manage Classes)
  const [instructors, setInstructors] = useState([]);
  const instructorById = useMemo(() => {
    const map = new Map();
    (Array.isArray(instructors) ? instructors : []).forEach((u) => {
      const keys = [u?.clerk_id, u?.clerkId, u?.clerkID, u?.user_id, u?.userId, u?.id]
        .filter((v) => v !== null && v !== undefined && `${v}`.trim() !== "")
        .map((v) => String(v));
      keys.forEach((k) => map.set(k, u));
    });
    return map;
  }, [instructors]);

  useEffect(() => {
    if (!isLoaded) return;
    if (!isPrivilegedModeOn) {
      setInstructors([]);
      return;
    }
    let cancelled = false;

    (async () => {
      try {
        const token = await getToken();
        if (!token) {
          if (!cancelled) setInstructors([]);
          return;
        }

        const reqHeaders = {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        };

        const result = await fetchAllPaginatedItems({
          url: api_endpoint + `/user/users/role/${"Instructor"}`,
          token,
          selectItems: (data) => data?.users ?? [],
          mode: "skip",
          pageSize: 200,
          headers: reqHeaders,
        });

        if (!cancelled) setInstructors(Array.isArray(result) ? result : []);
      } catch {
        if (!cancelled) setInstructors([]);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [getToken, isLoaded, isPrivilegedModeOn]);
  const [pendingFilterBy, setPendingFilterBy] = useState("all");
  const [pendingProjectTypeFilter, setPendingProjectTypeFilter] =
    useState("all");
  const [pendingSortBy, setPendingSortBy] = useState("submissionDate");
  const [instructorFilter, setInstructorFilter] = useState("ALL");
  const [pendingInstructorFilter, setPendingInstructorFilter] = useState("ALL");
  const didDebugFetchReviewsRef = useRef(false);

  const isExhaustiveInstructorModeOn = useMemo(() => {
    return Boolean(isPrivilegedModeOn && instructorFilter && instructorFilter !== "ALL");
  }, [instructorFilter, isPrivilegedModeOn]);

  // Exhaustive mode cache: store instructor-matched items so switching pages is instant.
  // Keyed by instructor clerk id.
  const exhaustiveCacheRef = useRef({
    unreviewed: new Map(),
    reviewed: new Map(),
  });
  const unreviewedExhaustiveRef = useRef({ key: null, promise: null, runId: 0 });
  const reviewedExhaustiveRef = useRef({ key: null, promise: null, runId: 0 });

  const [unreviewedPage, setUnreviewedPage] = useState(1);
  const [unreviewedRowsPerPage, setUnreviewedRowsPerPage] = useState(10);
  const [unreviewedPagination, setUnreviewedPagination] = useState(() =>
    normalizePaginationMeta(null, {
      current_page: 1,
      page_size: 10,
      total_count: 0,
      total_pages: 1,
    })
  );

  const [reviewedPage, setReviewedPage] = useState(1);
  const [reviewedRowsPerPage, setReviewedRowsPerPage] = useState(10);
  const [reviewedPagination, setReviewedPagination] = useState(() =>
    normalizePaginationMeta(null, {
      current_page: 1,
      page_size: 10,
      total_count: 0,
      total_pages: 1,
    })
  );

  const HYDRATE_FIRST_BATCH = 6;
  const HYDRATE_BATCH_SIZE = 4;

  const [unreviewedHydratingRest, setUnreviewedHydratingRest] = useState(false);
  const [reviewedHydratingRest, setReviewedHydratingRest] = useState(false);

  // Backend pagination uses skip/limit across all endpoints.
  // Keep UI rows-per-page options capped at 50 (original behavior).
  useEffect(() => {
    const cap = 50;
    setUnreviewedRowsPerPage((prev) => (Number(prev) > cap ? cap : prev));
    setReviewedRowsPerPage((prev) => (Number(prev) > cap ? cap : prev));
  }, [isPrivilegedModeOn]);

  const projectDetailsCacheRef = useRef(new Map());
  const projectDetailsInFlightRef = useRef(new Map());
  const tmReviewsCacheRef = useRef(new Map());
  const tmReviewsInFlightRef = useRef(new Map());

  const fetchProjectDetailsCached = useCallback(
    async ({ projectId, token }) => {
      const key = String(projectId || "").trim();
      if (!key) return null;

      if (projectDetailsCacheRef.current.has(key)) {
        return projectDetailsCacheRef.current.get(key);
      }

      if (projectDetailsInFlightRef.current.has(key)) {
        return projectDetailsInFlightRef.current.get(key);
      }

      const promise = (async () => {
        try {
          const reqConfig = {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          };
          const res = await axios.get(api_endpoint + `/project/get/${key}`, reqConfig);
          const project = res?.data?.project ?? res?.data?.data?.project ?? null;
          projectDetailsCacheRef.current.set(key, project);
          return project;
        } catch {
          projectDetailsCacheRef.current.set(key, null);
          return null;
        } finally {
          projectDetailsInFlightRef.current.delete(key);
        }
      })();

      projectDetailsInFlightRef.current.set(key, promise);
      return promise;
    },
    []
  );

  const normalizeTmReview = useCallback((value) => {
    if (!value || typeof value !== "object") return null;
    const nested = value.review && typeof value.review === "object" ? value.review : null;
    const scoreRaw = value.score ?? nested?.score;
    const scoreParsed = typeof scoreRaw === "string" ? Number(scoreRaw) : scoreRaw;
    const normalizedScore =
      typeof scoreParsed === "number" && Number.isFinite(scoreParsed) ? scoreParsed : scoreParsed ?? null;

    return {
      ...value,
      id: value.id ?? nested?.id ?? null,
      reviewed_at: value.reviewed_at ?? nested?.reviewed_at ?? null,
      reviewed_by: value.reviewed_by ?? nested?.reviewed_by ?? null,
      project_id: value.project_id ?? nested?.project_id ?? null,
      student_id: value.student_id ?? nested?.student_id ?? null,
      score: normalizedScore,
      feedback: value.feedback ?? nested?.feedback ?? "",
      is_passed: value.is_passed ?? nested?.is_passed,
      reviewer: value.reviewer ?? nested?.reviewer,
      review: nested ?? value.review,
    };
  }, []);

  const pickLatestTmReview = useCallback((reviews) => {
    if (!Array.isArray(reviews) || reviews.length === 0) return null;
    const sorted = [...reviews].sort((a, b) => {
      const aTime = a?.reviewed_at ? Date.parse(a.reviewed_at) : NaN;
      const bTime = b?.reviewed_at ? Date.parse(b.reviewed_at) : NaN;
      if (!Number.isNaN(aTime) && !Number.isNaN(bTime) && aTime !== bTime) return aTime - bTime;
      const aId = typeof a?.id === "number" ? a.id : -1;
      const bId = typeof b?.id === "number" ? b.id : -1;
      return aId - bId;
    });
    return sorted[sorted.length - 1] ?? null;
  }, []);

  const fetchLatestTmReviewCached = useCallback(
    async ({ projectId, studentId, token }) => {
      const p = String(projectId || "").trim();
      const s = String(studentId || "").trim();
      if (!p || !s) return null;

      const key = `${p}|${s}`;
      if (tmReviewsCacheRef.current.has(key)) return tmReviewsCacheRef.current.get(key);
      if (tmReviewsInFlightRef.current.has(key)) return tmReviewsInFlightRef.current.get(key);

      const promise = (async () => {
        try {
          const reqConfig = {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          };

          const res = await axios.post(
            api_endpoint + "/project/get/reviews",
            { project_id: p, student_id: s },
            reqConfig
          );

          const tm = res?.data?.reviews?.tm_reviews;
          const latest = normalizeTmReview(pickLatestTmReview(tm));
          tmReviewsCacheRef.current.set(key, latest);
          return latest;
        } catch {
          tmReviewsCacheRef.current.set(key, null);
          return null;
        } finally {
          tmReviewsInFlightRef.current.delete(key);
        }
      })();

      tmReviewsInFlightRef.current.set(key, promise);
      return promise;
    },
    [normalizeTmReview, pickLatestTmReview]
  );

  const hydrateInBatches = useCallback(async (items, batchSize, hydrateItem) => {
    const results = [];
    for (let i = 0; i < items.length; i += batchSize) {
      const slice = items.slice(i, i + batchSize);
      // Preserve order within the slice.
      const hydrated = await Promise.all(slice.map(hydrateItem));
      results.push(...hydrated);
    }
    return results;
  }, []);

  // Sync active tab from URL query param (preferred), then localStorage (fallback) on mount.
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const tabParam = params.get("tab");
      if (tabParam === "reviewed" || tabParam === "unreviewed") {
        setActiveTab(tabParam);
        return;
      }

      const stored = window.localStorage.getItem(TAB_STORAGE_KEY);
      if (stored === "reviewed" || stored === "unreviewed") {
        setActiveTab(stored);
      }
    } catch (e) {
      // ignore
    }
  }, []);

  // Persist active tab (URL + localStorage) when it changes.
  useEffect(() => {
    try {
      window.localStorage.setItem(TAB_STORAGE_KEY, activeTab);
      const params = new URLSearchParams(window.location.search);
      if (activeTab) {
        params.set("tab", activeTab);
        const newUrl = window.location.pathname + "?" + params.toString();
        window.history.replaceState({}, "", newUrl);
      }
    } catch (e) {
      // ignore
    }
  }, [activeTab]);

  // Lazy-mount tab panels: mount only when user visits.
  useEffect(() => {
    if (activeTab === "unreviewed") setShouldRenderUnreviewedTab(true);
    if (activeTab === "reviewed") setShouldRenderReviewedTab(true);
  }, [activeTab]);

  // useEffect(() => {
  //     setLoading(true)
  //  setTimeout(() => {
  //         setUnreviewedProjects(UnreviewedProjectsData);
  //         setReviewedProjects(ReviewedProjectsData);
  //         setLoading(false);
  //     }, 1000)
  // }, [])

  useEffect(() => {
    if (!isLoaded || !user) return;
    setUnreviewedPage(1);
    setReviewedPage(1);
    // Refetch when role-mode toggles change (admin/manager mode).
  }, [isLoaded, user, isPrivilegedModeOn]);
  useEffect(() => {
    setPendingFilterBy(filterBy);
  }, [filterBy]);
  useEffect(() => {
    setPendingProjectTypeFilter(projectTypeFilter);
  }, [projectTypeFilter]);
  useEffect(() => {
    setPendingSortBy(sortBy);
  }, [sortBy]);

  useEffect(() => {
    setPendingInstructorFilter(instructorFilter);
  }, [instructorFilter]);

  // When instructor filter changes in privileged mode, reset pagination to first page.
  useEffect(() => {
    if (!isPrivilegedModeOn) return;
    setUnreviewedPage(1);
    setReviewedPage(1);
  }, [instructorFilter, isPrivilegedModeOn]);

  useEffect(() => {
    if (isPrivilegedModeOn) return;
    if (instructorFilter !== "ALL") {
      setInstructorFilter("ALL");
      setPendingInstructorFilter("ALL");
    }
  }, [isPrivilegedModeOn, instructorFilter]);

  const unreviewedFetchRef = useRef({ key: null, promise: null, runId: 0 });
  const reviewedFetchRef = useRef({ key: null, promise: null, runId: 0 });
  const postAssignRefetchTimerRef = useRef(null);

  const getProjectDedupeKey = useCallback((projectData) => {
    const submission = projectData?.submission ?? null;
    const projectId =
      submission?.project_id ??
      submission?.projectId ??
      submission?.project?.id ??
      submission?.project?._id ??
      projectData?.project_id ??
      projectData?.projectId ??
      projectData?.id ??
      projectData?._id ??
      "";
    const studentId =
      submission?.user_id ??
      submission?.userId ??
      submission?.student_id ??
      submission?.studentId ??
      projectData?.user_id ??
      projectData?.userId ??
      projectData?.student_id ??
      projectData?.studentId ??
      "";
    const submittedAt =
      submission?.submitted_on ??
      submission?.submittedOn ??
      submission?.last_updated_at ??
      submission?.lastUpdatedAt ??
      projectData?.submitted_on ??
      projectData?.submittedOn ??
      projectData?.last_updated_at ??
      projectData?.lastUpdatedAt ??
      "";
    return `${String(projectId || "").trim()}|${String(studentId || "").trim()}|${String(submittedAt || "").trim()}`;
  }, []);

  const normalizeUserIdCandidate = useCallback((value) => {
    if (value === null || value === undefined) return "";

    if (typeof value === "string" || typeof value === "number" || typeof value === "bigint") {
      const str = String(value).trim();
      return str ? str : "";
    }

    if (typeof value === "object") {
      const obj = value;
      const candidates = [
        obj?.clerk_id,
        obj?.clerkId,
        obj?.clerkID,
        obj?.user_id,
        obj?.userId,
        obj?.id,
        obj?._id,
      ];
      for (const c of candidates) {
        const str = c !== null && c !== undefined ? String(c).trim() : "";
        if (str) return str;
      }
    }

    return "";
  }, []);

  const getInstructorIdFromProject = useCallback(
    (project) => {
      const p = project || {};
      const candidates = [
        // Unreviewed projects (admin endpoint): assigned reviewer lives on submission
        p?.submission?.assigned_reviewer,
        p?.submission?.assignedReviewer,
        p?.assigned_reviewer,
        p?.assignedReviewer,

        // Reviewed projects: sometimes includes review objects
        p?.reviews?.tm_reviews?.[0]?.reviewed_by,
        p?.tm_reviews?.[0]?.reviewed_by,
        p?.reviewed_by,
        p?.reviewedBy,
        p?.graded_by,
        p?.gradedBy,
        p?.submission?.graded_by,
        p?.submission?.gradedBy,

        // Fallbacks for other shapes
        p?.reviewer_id,
        p?.reviewerId,
        p?.graded_by_id,
        p?.gradedById,
        p?.tm_id,
        p?.tmId,
        p?.owner_id,
        p?.ownerId,
        p?.instructor_id,
        p?.instructorId,
        p?.submission?.tm_id,
        p?.submission?.tmId,
        p?.submission?.owner_id,
        p?.submission?.ownerId,
        p?.submission?.instructor_id,
        p?.submission?.instructorId,

        // Sometimes reviewer is embedded
        p?.reviewer,
        p?.reviews?.reviewer,
        p?.reviews?.tm_reviews?.[0]?.reviewer,
        p?.tm_reviews?.[0]?.reviewer,
      ];

      for (const candidate of candidates) {
        const id = normalizeUserIdCandidate(candidate);
        if (id) return id;
      }
      return "";
    },
    [normalizeUserIdCandidate]
  );

  useEffect(() => {
    return () => {
      if (postAssignRefetchTimerRef.current) {
        clearTimeout(postAssignRefetchTimerRef.current);
        postAssignRefetchTimerRef.current = null;
      }
    };
  }, []);

  const fetchUnreviewed = async (options = {}) => {
    if (isExhaustiveInstructorModeOn) {
      const { background = false } = options || {};
      const instructorId = String(instructorFilter || "").trim();
      if (!instructorId) return Promise.resolve();

      const fetchKey = `unreviewed|exhaustive|${instructorId}`;
      if (unreviewedExhaustiveRef.current?.promise && unreviewedExhaustiveRef.current.key === fetchKey) {
        return unreviewedExhaustiveRef.current.promise;
      }

      const runId = (unreviewedExhaustiveRef.current.runId += 1);
      const shouldShowSpinner =
        !background && (Array.isArray(unreviewedProjects) ? unreviewedProjects.length : 0) === 0;
      if (shouldShowSpinner) setUnreviewedLoading(true);
      if (!background) setUnreviewedHydratingRest(false);

      const promise = (async () => {
        setError("");
        try {
          const token = await getToken();
          if (!token) return;

          const reqConfig = {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          };

          const cacheMap = exhaustiveCacheRef.current.unreviewed;
          const maxCached = 5;
          if (!cacheMap.has(instructorId) && cacheMap.size >= maxCached) {
            // Drop the oldest cached instructor bucket.
            const firstKey = cacheMap.keys().next().value;
            if (firstKey) cacheMap.delete(firstKey);
          }

          const entry = cacheMap.get(instructorId) ?? {
            items: [],
            nextSkip: 0,
            done: false,
            pageSize: 200,
            seen: new Set(),
          };
          cacheMap.set(instructorId, entry);

          const path = "/project/ungraded/admin/all";
          const limit = Number(entry.pageSize) || 200;
          const needed = Math.max(1, unreviewedPage) * Math.max(1, unreviewedRowsPerPage);
          const buffer = Math.max(1, unreviewedRowsPerPage);
          const target = needed + buffer;

          // Foreground: fetch until we can render the requested page (plus small buffer).
          let pagesFetched = 0;
          while (
            runId === unreviewedExhaustiveRef.current.runId &&
            !entry.done &&
            entry.items.length < target &&
            pagesFetched < 25
          ) {
            const skip = Number(entry.nextSkip) || 0;
            const payload = (
              await axios.get(api_endpoint + path, {
                ...reqConfig,
                params: { skip, limit },
              })
            ).data;

            const parsed = parseProjectsResponse(payload);
            const raw = Array.isArray(parsed.data) ? parsed.data : [];

            entry.nextSkip = skip + limit;
            if (raw.length === 0 || raw.length < limit) entry.done = true;

            const matches = raw.filter((p) => {
              const id = String(getInstructorIdFromProject(p) || "").trim();
              return id && id === instructorId;
            });

            for (const m of matches) {
              const k = getProjectDedupeKey(m);
              if (!entry.seen.has(k)) {
                entry.seen.add(k);
                entry.items.push(m);
              }
            }

            pagesFetched += 1;
            if (raw.length === 0) break;
          }

          const totalCountKnown = entry.items.length;
          const totalPagesKnown = Math.max(1, Math.ceil(totalCountKnown / Math.max(1, unreviewedRowsPerPage)));
          const hasMore = !entry.done;

          // Best-effort pagination while still fetching in background.
          const optimisticTotalPages = hasMore
            ? Math.max(totalPagesKnown, unreviewedPage + 1)
            : totalPagesKnown;

          if (runId === unreviewedExhaustiveRef.current.runId) {
            setUnreviewedPagination(
              normalizePaginationMeta(
                {
                  current_page: unreviewedPage,
                  page_size: unreviewedRowsPerPage,
                  total_count: hasMore ? Math.max(totalCountKnown, needed) : totalCountKnown,
                  total_pages: optimisticTotalPages,
                  has_next: hasMore ? true : unreviewedPage < totalPagesKnown,
                  has_previous: unreviewedPage > 1,
                },
                {
                  current_page: unreviewedPage,
                  page_size: unreviewedRowsPerPage,
                  total_count: 0,
                  total_pages: 1,
                }
              )
            );
          }

          const startIdx = (unreviewedPage - 1) * unreviewedRowsPerPage;
          const endIdx = startIdx + unreviewedRowsPerPage;
          const pageItems = entry.items.slice(startIdx, endIdx);

          const hydrateItem = async (projectData) => {
            const submission = projectData?.submission || null;
            const projectId =
              submission?.project_id ??
              submission?.projectId ??
              submission?.project?.id ??
              submission?.project?.project_id ??
              submission?.project?.projectId ??
              null;

            if (!projectId) return projectData;

            const details = await fetchProjectDetailsCached({ projectId, token });
            return {
              ...projectData,
              submission: {
                ...(submission || {}),
                project: details || submission?.project || null,
              },
            };
          };

          const firstSlice = pageItems.slice(0, HYDRATE_FIRST_BATCH);
          const restSlice = pageItems.slice(HYDRATE_FIRST_BATCH);

          const firstHydrated = await hydrateInBatches(firstSlice, HYDRATE_BATCH_SIZE, hydrateItem);
          if (runId !== unreviewedExhaustiveRef.current.runId) return;

          setUnreviewedProjects(firstHydrated);
          setUnreviewedLoading(false);

          if (restSlice.length > 0) {
            setUnreviewedHydratingRest(true);
            const restHydrated = await hydrateInBatches(restSlice, HYDRATE_BATCH_SIZE, hydrateItem);
            if (runId !== unreviewedExhaustiveRef.current.runId) return;
            setUnreviewedProjects((prev) => [...(Array.isArray(prev) ? prev : []), ...restHydrated]);
            setUnreviewedHydratingRest(false);
          }

          // Background: finish fetching remaining pages so totals become accurate.
          if (!entry.done) {
            (async () => {
              try {
                let guard = 0;
                while (runId === unreviewedExhaustiveRef.current.runId && !entry.done && guard < 200) {
                  const skip = Number(entry.nextSkip) || 0;
                  const payload = (
                    await axios.get(api_endpoint + path, {
                      ...reqConfig,
                      params: { skip, limit },
                    })
                  ).data;
                  const parsed = parseProjectsResponse(payload);
                  const raw = Array.isArray(parsed.data) ? parsed.data : [];

                  entry.nextSkip = skip + limit;
                  if (raw.length === 0 || raw.length < limit) entry.done = true;

                  const matches = raw.filter((p) => {
                    const id = String(getInstructorIdFromProject(p) || "").trim();
                    return id && id === instructorId;
                  });
                  for (const m of matches) {
                    const k = getProjectDedupeKey(m);
                    if (!entry.seen.has(k)) {
                      entry.seen.add(k);
                      entry.items.push(m);
                    }
                  }

                  guard += 1;
                  if (raw.length === 0) break;
                }

                if (runId !== unreviewedExhaustiveRef.current.runId) return;
                const finalCount = entry.items.length;
                const finalPages = Math.max(1, Math.ceil(finalCount / Math.max(1, unreviewedRowsPerPage)));
                setUnreviewedPagination(
                  normalizePaginationMeta(
                    {
                      current_page: unreviewedPage,
                      page_size: unreviewedRowsPerPage,
                      total_count: finalCount,
                      total_pages: finalPages,
                      has_next: unreviewedPage < finalPages,
                      has_previous: unreviewedPage > 1,
                    },
                    {
                      current_page: unreviewedPage,
                      page_size: unreviewedRowsPerPage,
                      total_count: 0,
                      total_pages: 1,
                    }
                  )
                );
              } catch {
                // Ignore background completion failures.
              }
            })();
          }
        } catch (error) {
          console.error("Error fetching unreviewed projects (exhaustive):", error);
          if (runId === unreviewedExhaustiveRef.current.runId) {
            setError("Failed to fetch unreviewed projects.");
            setUnreviewedProjects([]);
          }
        } finally {
          if (runId === unreviewedExhaustiveRef.current.runId) {
            setUnreviewedLoading(false);
            if (!background) setUnreviewedHydratingRest(false);
          }
        }
      })();

      unreviewedExhaustiveRef.current = { ...unreviewedExhaustiveRef.current, key: fetchKey, promise };
      promise.finally(() => {
        if (unreviewedExhaustiveRef.current?.promise === promise) {
          unreviewedExhaustiveRef.current = { ...unreviewedExhaustiveRef.current, key: null, promise: null };
        }
      });
      return promise;
    }

    const { background = false } = options || {};
    const effectiveUserId = userId || user?.id;
    const modeKey = isPrivilegedModeOn ? "priv" : "user";
    const fetchKey = `unreviewed|${modeKey}|${String(effectiveUserId || "")}|${unreviewedPage}|${unreviewedRowsPerPage}`;


    if (unreviewedFetchRef.current?.promise && unreviewedFetchRef.current.key === fetchKey) {
      return unreviewedFetchRef.current.promise;
    }

    const runId = (unreviewedFetchRef.current.runId += 1);
    const shouldShowSpinner =
      !background &&
      (Array.isArray(unreviewedProjects) ? unreviewedProjects.length : 0) === 0;
    if (shouldShowSpinner) setUnreviewedLoading(true);
    if (!background) setUnreviewedHydratingRest(false);

    const promise = (async () => {
      setError("");
      try {
        const token = await getToken();
        if (!token) return;

        if (!effectiveUserId) {
          console.warn("[fetchUnreviewed] Missing user id", { userId, userObjectId: user?.id });
          if (runId === unreviewedFetchRef.current.runId) setUnreviewedProjects([]);
          return;
        }

        const reqConfig = {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        };

        const path = isPrivilegedModeOn
          ? "/project/ungraded/admin/all"
          : `/project/ungraded/${effectiveUserId}`;
        const url = api_endpoint + path;

        const params = isPrivilegedModeOn
          ? {
              skip: (unreviewedPage - 1) * unreviewedRowsPerPage,
              limit: unreviewedRowsPerPage,
            }
          : {
              skip: (unreviewedPage - 1) * unreviewedRowsPerPage,
              limit: unreviewedRowsPerPage,
            };

        const payload = (await axios.get(url, { ...reqConfig, params })).data;
        const parsed = parseProjectsResponse(payload);
        const normalizedMeta = normalizePaginationMeta(parsed.pagination, {
          current_page: unreviewedPage,
          page_size: unreviewedRowsPerPage,
          total_count: 0,
          total_pages: 1,
        });

        // Fallback meta when backend doesn't return totals (keeps pagination usable).
        const itemsOnPage = Array.isArray(parsed.data) ? parsed.data.length : 0;
        if (!parsed.pagination) {
          const hasNextGuess = itemsOnPage === unreviewedRowsPerPage;
          normalizedMeta.total_pages = hasNextGuess ? unreviewedPage + 1 : unreviewedPage;
          normalizedMeta.total_count = Math.max(
            normalizedMeta.total_count,
            (unreviewedPage - 1) * unreviewedRowsPerPage + itemsOnPage
          );
          normalizedMeta.has_next = hasNextGuess;
          normalizedMeta.has_previous = unreviewedPage > 1;
        }

        if (runId === unreviewedFetchRef.current.runId) {
          setUnreviewedPagination(normalizedMeta);
          const raw = Array.isArray(parsed.data) ? parsed.data : [];

          // Progressive hydration: show first batch fully-hydrated, then append remaining.
          const hydrateItem = async (projectData) => {
            const submission = projectData?.submission || null;
            const projectId =
              submission?.project_id ??
              submission?.projectId ??
              submission?.project?.id ??
              submission?.project?.project_id ??
              submission?.project?.projectId ??
              null;

            if (!projectId) return projectData;

            const details = await fetchProjectDetailsCached({ projectId, token });
            return {
              ...projectData,
              submission: {
                ...(submission || {}),
                project: details || submission?.project || null,
              },
            };
          };

          const firstSlice = raw.slice(0, HYDRATE_FIRST_BATCH);
          const restSlice = raw.slice(HYDRATE_FIRST_BATCH);

          const firstHydrated = await hydrateInBatches(firstSlice, HYDRATE_BATCH_SIZE, hydrateItem);
          if (runId !== unreviewedFetchRef.current.runId) return;

          setUnreviewedProjects(firstHydrated);
          setUnreviewedLoading(false);

          if (restSlice.length > 0) {
            setUnreviewedHydratingRest(true);
            const restHydrated = await hydrateInBatches(restSlice, HYDRATE_BATCH_SIZE, hydrateItem);
            if (runId !== unreviewedFetchRef.current.runId) return;
            setUnreviewedProjects((prev) => [...(Array.isArray(prev) ? prev : []), ...restHydrated]);
            setUnreviewedHydratingRest(false);
          }
        }
      } catch (error) {
        console.error("Error fetching unreviewed projects:", error);
        if (runId === unreviewedFetchRef.current.runId) {
          setError("Failed to fetch unreviewed projects.");
          setUnreviewedProjects([]);
        }
      } finally {
        if (runId === unreviewedFetchRef.current.runId) {
          setUnreviewedLoading(false);
          if (!background) setUnreviewedHydratingRest(false);
        }
      }
    })();

    unreviewedFetchRef.current = { ...unreviewedFetchRef.current, key: fetchKey, promise };
    promise.finally(() => {
      if (unreviewedFetchRef.current?.promise === promise) {
        unreviewedFetchRef.current = { ...unreviewedFetchRef.current, key: null, promise: null };
      }
    });

    return promise;
  };

  // Note: Removed dev-only review debug fetch. The page now preloads latest TM reviews.
  

  const fetchReviewed = async (options = {}) => {
    if (isExhaustiveInstructorModeOn) {
      const { background = false } = options || {};
      const instructorId = String(instructorFilter || "").trim();
      if (!instructorId) return Promise.resolve();

      const fetchKey = `reviewed|exhaustive|${instructorId}`;
      if (reviewedExhaustiveRef.current?.promise && reviewedExhaustiveRef.current.key === fetchKey) {
        return reviewedExhaustiveRef.current.promise;
      }

      const runId = (reviewedExhaustiveRef.current.runId += 1);
      const shouldShowSpinner =
        !background && (Array.isArray(reviewedProjects) ? reviewedProjects.length : 0) === 0;
      if (shouldShowSpinner) setReviewedLoading(true);
      if (!background) setReviewedHydratingRest(false);

      const promise = (async () => {
        setError("");
        try {
          const token = await getToken();
          if (!token) return;

          const reqConfig = {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          };

          const cacheMap = exhaustiveCacheRef.current.reviewed;
          const maxCached = 5;
          if (!cacheMap.has(instructorId) && cacheMap.size >= maxCached) {
            const firstKey = cacheMap.keys().next().value;
            if (firstKey) cacheMap.delete(firstKey);
          }

          const entry = cacheMap.get(instructorId) ?? {
            items: [],
            nextSkip: 0,
            done: false,
            pageSize: 200,
            seen: new Set(),
          };
          cacheMap.set(instructorId, entry);

          const path = "/project/graded/admin/all";
          const limit = Number(entry.pageSize) || 200;
          const needed = Math.max(1, reviewedPage) * Math.max(1, reviewedRowsPerPage);
          const buffer = Math.max(1, reviewedRowsPerPage);
          const target = needed + buffer;

          let pagesFetched = 0;
          while (
            runId === reviewedExhaustiveRef.current.runId &&
            !entry.done &&
            entry.items.length < target &&
            pagesFetched < 25
          ) {
            const skip = Number(entry.nextSkip) || 0;
            const payload = (
              await axios.get(api_endpoint + path, {
                ...reqConfig,
                params: { skip, limit },
              })
            ).data;

            const parsed = parseProjectsResponse(payload);
            const raw = Array.isArray(parsed.data) ? parsed.data : [];

            entry.nextSkip = skip + limit;
            if (raw.length === 0 || raw.length < limit) entry.done = true;

            const matches = raw.filter((p) => {
              const id = String(getInstructorIdFromProject(p) || "").trim();
              return id && id === instructorId;
            });

            for (const m of matches) {
              const k = getProjectDedupeKey(m);
              if (!entry.seen.has(k)) {
                entry.seen.add(k);
                entry.items.push(m);
              }
            }

            pagesFetched += 1;
            if (raw.length === 0) break;
          }

          const totalCountKnown = entry.items.length;
          const totalPagesKnown = Math.max(1, Math.ceil(totalCountKnown / Math.max(1, reviewedRowsPerPage)));
          const hasMore = !entry.done;
          const optimisticTotalPages = hasMore
            ? Math.max(totalPagesKnown, reviewedPage + 1)
            : totalPagesKnown;

          if (runId === reviewedExhaustiveRef.current.runId) {
            setReviewedPagination(
              normalizePaginationMeta(
                {
                  current_page: reviewedPage,
                  page_size: reviewedRowsPerPage,
                  total_count: hasMore ? Math.max(totalCountKnown, needed) : totalCountKnown,
                  total_pages: optimisticTotalPages,
                  has_next: hasMore ? true : reviewedPage < totalPagesKnown,
                  has_previous: reviewedPage > 1,
                },
                {
                  current_page: reviewedPage,
                  page_size: reviewedRowsPerPage,
                  total_count: 0,
                  total_pages: 1,
                }
              )
            );
          }

          const startIdx = (reviewedPage - 1) * reviewedRowsPerPage;
          const endIdx = startIdx + reviewedRowsPerPage;
          const pageItems = entry.items.slice(startIdx, endIdx);

          const hydrateItem = async (projectData) => {
            const submission = projectData?.submission || null;
            const projectId =
              submission?.project_id ??
              submission?.projectId ??
              submission?.project?.id ??
              submission?.project?.project_id ??
              submission?.project?.projectId ??
              null;
            const studentId =
              submission?.user_id ??
              submission?.userId ??
              submission?.student_id ??
              submission?.studentId ??
              null;

            let details = null;
            if (projectId) {
              details = await fetchProjectDetailsCached({ projectId, token });
            }

            let latestTmReview = null;
            if (projectId && studentId) {
              latestTmReview = await fetchLatestTmReviewCached({ projectId, studentId, token });
            }

            return {
              ...projectData,
              tmReview: latestTmReview,
              submission: {
                ...(submission || {}),
                project: details || submission?.project || null,
              },
            };
          };

          const firstSlice = pageItems.slice(0, HYDRATE_FIRST_BATCH);
          const restSlice = pageItems.slice(HYDRATE_FIRST_BATCH);

          const firstHydrated = await hydrateInBatches(firstSlice, HYDRATE_BATCH_SIZE, hydrateItem);
          if (runId !== reviewedExhaustiveRef.current.runId) return;

          setReviewedProjects(firstHydrated);
          setReviewedLoading(false);

          if (restSlice.length > 0) {
            setReviewedHydratingRest(true);
            const restHydrated = await hydrateInBatches(restSlice, HYDRATE_BATCH_SIZE, hydrateItem);
            if (runId !== reviewedExhaustiveRef.current.runId) return;
            setReviewedProjects((prev) => [...(Array.isArray(prev) ? prev : []), ...restHydrated]);
            setReviewedHydratingRest(false);
          }

          if (!entry.done) {
            (async () => {
              try {
                let guard = 0;
                while (runId === reviewedExhaustiveRef.current.runId && !entry.done && guard < 200) {
                  const skip = Number(entry.nextSkip) || 0;
                  const payload = (
                    await axios.get(api_endpoint + path, {
                      ...reqConfig,
                      params: { skip, limit },
                    })
                  ).data;
                  const parsed = parseProjectsResponse(payload);
                  const raw = Array.isArray(parsed.data) ? parsed.data : [];

                  entry.nextSkip = skip + limit;
                  if (raw.length === 0 || raw.length < limit) entry.done = true;

                  const matches = raw.filter((p) => {
                    const id = String(getInstructorIdFromProject(p) || "").trim();
                    return id && id === instructorId;
                  });
                  for (const m of matches) {
                    const k = getProjectDedupeKey(m);
                    if (!entry.seen.has(k)) {
                      entry.seen.add(k);
                      entry.items.push(m);
                    }
                  }

                  guard += 1;
                  if (raw.length === 0) break;
                }

                if (runId !== reviewedExhaustiveRef.current.runId) return;
                const finalCount = entry.items.length;
                const finalPages = Math.max(1, Math.ceil(finalCount / Math.max(1, reviewedRowsPerPage)));
                setReviewedPagination(
                  normalizePaginationMeta(
                    {
                      current_page: reviewedPage,
                      page_size: reviewedRowsPerPage,
                      total_count: finalCount,
                      total_pages: finalPages,
                      has_next: reviewedPage < finalPages,
                      has_previous: reviewedPage > 1,
                    },
                    {
                      current_page: reviewedPage,
                      page_size: reviewedRowsPerPage,
                      total_count: 0,
                      total_pages: 1,
                    }
                  )
                );
              } catch {
                // Ignore background completion failures.
              }
            })();
          }
        } catch (error) {
          console.error("Error fetching reviewed projects (exhaustive):", error);
          if (runId === reviewedExhaustiveRef.current.runId) {
            setError("Failed to fetch reviewed projects.");
            setReviewedProjects([]);
          }
        } finally {
          if (runId === reviewedExhaustiveRef.current.runId) {
            setReviewedLoading(false);
            if (!background) setReviewedHydratingRest(false);
          }
        }
      })();

      reviewedExhaustiveRef.current = { ...reviewedExhaustiveRef.current, key: fetchKey, promise };
      promise.finally(() => {
        if (reviewedExhaustiveRef.current?.promise === promise) {
          reviewedExhaustiveRef.current = { ...reviewedExhaustiveRef.current, key: null, promise: null };
        }
      });
      return promise;
    }

    const { background = false } = options || {};
    const effectiveUserId = userId || user?.id;
    const modeKey = isPrivilegedModeOn ? "priv" : "user";
    const fetchKey = `reviewed|${modeKey}|${String(effectiveUserId || "")}|${reviewedPage}|${reviewedRowsPerPage}`;


    if (reviewedFetchRef.current?.promise && reviewedFetchRef.current.key === fetchKey) {
      return reviewedFetchRef.current.promise;
    }

    const runId = (reviewedFetchRef.current.runId += 1);
    const shouldShowSpinner =
      !background && (Array.isArray(reviewedProjects) ? reviewedProjects.length : 0) === 0;
    if (shouldShowSpinner) setReviewedLoading(true);
    if (!background) setReviewedHydratingRest(false);

    const promise = (async () => {
      setError("");
      try {
        const token = await getToken();
        if (!token) return;

        if (!effectiveUserId) {
          console.warn("[fetchReviewed] Missing user id", { userId, userObjectId: user?.id });
          if (runId === reviewedFetchRef.current.runId) setReviewedProjects([]);
          return;
        }

        const reqConfig = {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        };

        const path = isPrivilegedModeOn
          ? "/project/graded/admin/all"
          : `/project/graded/${effectiveUserId}`;
        const url = api_endpoint + path;

        const params = isPrivilegedModeOn
          ? {
              skip: (reviewedPage - 1) * reviewedRowsPerPage,
              limit: reviewedRowsPerPage,
            }
          : {
              skip: (reviewedPage - 1) * reviewedRowsPerPage,
              limit: reviewedRowsPerPage,
            };

        const payload = (await axios.get(url, { ...reqConfig, params })).data;
        console.log(
  `[REVIEWED RAW] mode=${isPrivilegedModeOn ? "privileged" : "normal"} endpoint=${path}`,
  payload
);
        const parsed = parseProjectsResponse(payload);
        const normalizedMeta = normalizePaginationMeta(parsed.pagination, {
          current_page: reviewedPage,
          page_size: reviewedRowsPerPage,
          total_count: 0,
          total_pages: 1,
        });

        // Fallback meta when backend doesn't return totals (keeps pagination usable).
        const itemsOnPage = Array.isArray(parsed.data) ? parsed.data.length : 0;
        if (!parsed.pagination) {
          const hasNextGuess = itemsOnPage === reviewedRowsPerPage;
          normalizedMeta.total_pages = hasNextGuess ? reviewedPage + 1 : reviewedPage;
          normalizedMeta.total_count = Math.max(
            normalizedMeta.total_count,
            (reviewedPage - 1) * reviewedRowsPerPage + itemsOnPage
          );
          normalizedMeta.has_next = hasNextGuess;
          normalizedMeta.has_previous = reviewedPage > 1;
        }

        if (runId === reviewedFetchRef.current.runId) {
          setReviewedPagination(normalizedMeta);
          const raw = Array.isArray(parsed.data) ? parsed.data : [];

          // Progressive hydration: project details + latest TM review before rendering cards.
          const hydrateItem = async (projectData) => {
            const submission = projectData?.submission || null;
            const projectId =
              submission?.project_id ??
              submission?.projectId ??
              submission?.project?.id ??
              submission?.project?.project_id ??
              submission?.project?.projectId ??
              null;
            const studentId =
              submission?.user_id ??
              submission?.userId ??
              submission?.student_id ??
              submission?.studentId ??
              null;

            let details = null;
            if (projectId) {
              details = await fetchProjectDetailsCached({ projectId, token });
            }

            let latestTmReview = null;
            if (projectId && studentId) {
              latestTmReview = await fetchLatestTmReviewCached({ projectId, studentId, token });
            }

            return {
              ...projectData,
              tmReview: latestTmReview,
              submission: {
                ...(submission || {}),
                project: details || submission?.project || null,
              },
            };
          };

          const firstSlice = raw.slice(0, HYDRATE_FIRST_BATCH);
          const restSlice = raw.slice(HYDRATE_FIRST_BATCH);

          const firstHydrated = await hydrateInBatches(firstSlice, HYDRATE_BATCH_SIZE, hydrateItem);
          if (runId !== reviewedFetchRef.current.runId) return;

          setReviewedProjects(firstHydrated);
          setReviewedLoading(false);

          if (restSlice.length > 0) {
            setReviewedHydratingRest(true);
            const restHydrated = await hydrateInBatches(restSlice, HYDRATE_BATCH_SIZE, hydrateItem);
            if (runId !== reviewedFetchRef.current.runId) return;
            setReviewedProjects((prev) => [...(Array.isArray(prev) ? prev : []), ...restHydrated]);
            setReviewedHydratingRest(false);
          }
        }
      } catch (error) {
        if (runId === reviewedFetchRef.current.runId) {
          setError("Failed to fetch reviewed projects.");
          setReviewedProjects([]);
        }
      } finally {
        if (runId === reviewedFetchRef.current.runId) {
          setReviewedLoading(false);
          if (!background) setReviewedHydratingRest(false);
        }
      }
    })();

    reviewedFetchRef.current = { ...reviewedFetchRef.current, key: fetchKey, promise };
    promise.finally(() => {
      if (reviewedFetchRef.current?.promise === promise) {
        reviewedFetchRef.current = { ...reviewedFetchRef.current, key: null, promise: null };
      }
    });

    return promise;
  };

  useEffect(() => {
    if (!isLoaded || !user) return;
    if (activeTab !== "unreviewed") return;
    fetchUnreviewed();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, isLoaded, user, isPrivilegedModeOn, instructorFilter, unreviewedPage, unreviewedRowsPerPage]);

  useEffect(() => {
    if (!isLoaded || !user) return;
    if (activeTab !== "reviewed") return;
    fetchReviewed();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, isLoaded, user, isPrivilegedModeOn, instructorFilter, reviewedPage, reviewedRowsPerPage]);

  // Background prefetch for the other tab after initial paint.
  useEffect(() => {
    if (!isLoaded || !user) return;
    if (isExhaustiveInstructorModeOn) return;
    const t = window.setTimeout(() => {
      if (activeTab === "unreviewed") {
        const hasReviewed = (Array.isArray(reviewedProjects) ? reviewedProjects.length : 0) > 0;
        if (!hasReviewed) fetchReviewed({ background: true });
      } else {
        const hasUnreviewed = (Array.isArray(unreviewedProjects) ? unreviewedProjects.length : 0) > 0;
        if (!hasUnreviewed) fetchUnreviewed({ background: true });
      }
    }, 350);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, isLoaded, user, isPrivilegedModeOn, isExhaustiveInstructorModeOn]);

  const fetchCourses = async () => {
    try {
      const normalizeCoursesPayload = (payload) => {
        if (Array.isArray(payload)) return payload;
        if (Array.isArray(payload?.courses)) return payload.courses;
        if (Array.isArray(payload?.items)) return payload.items;
        if (Array.isArray(payload?.data)) return payload.data;
        return [];
      };
      const normalizeCourseKeyLocal = (value) =>
        String(value || "")
          .trim()
          .toLowerCase()
          .replace(/[_-]+/g, " ")
          .replace(/[^\w\s]/g, " ")
          .replace(/\s+/g, " ");
      const getCourseIdLocal = (course) => {
        const id = course?.id ?? course?.course_id ?? course?.courseId;
        return id != null && `${id}`.trim() !== "" ? String(id) : "";
      };
      const getCourseSlugLocal = (course) => {
        const slug =
          course?.slug ??
          course?.course_slug ??
          course?.courseSlug ??
          course?.code ??
          course?.course_code ??
          course?.courseCode ??
          course?.abbr ??
          course?.course_abbr ??
          course?.courseAbbr ??
          "";
        const value = String(slug || "").trim();
        return value;
      };
      const getCourseNameLocal = (course) =>
        String(
          course?.title ??
            course?.name ??
            course?.course_title ??
            course?.courseTitle ??
            course?.course_name ??
            course?.courseName ??
            course?.display_name ??
            course?.displayName ??
            ""
        ).trim();
      const getCourseQualityLocal = (course) => {
        let score = 0;
        if (getCourseSlugLocal(course)) score += 8;
        if (getCourseIdLocal(course)) score += 4;
        if (normalizeCourseKeyLocal(getCourseNameLocal(course))) score += 2;
        return score;
      };
      const mergeCourseLists = (lists) => {
        const bestByKey = new Map();

        lists
          .flat()
          .filter((course) => course && typeof course === "object")
          .forEach((course) => {
            const idKey = getCourseIdLocal(course);
            const slugKey = normalizeCourseKeyLocal(getCourseSlugLocal(course));
            const nameKey = normalizeCourseKeyLocal(getCourseNameLocal(course));
            const key = idKey
              ? `id:${idKey}`
              : slugKey
              ? `slug:${slugKey}`
              : nameKey
              ? `name:${nameKey}`
              : "";
            if (!key) return;

            const existing = bestByKey.get(key);
            if (!existing) {
              bestByKey.set(key, course);
              return;
            }

            const existingScore = getCourseQualityLocal(existing);
            const nextScore = getCourseQualityLocal(course);
            if (nextScore >= existingScore) {
              bestByKey.set(key, { ...existing, ...course });
            } else {
              bestByKey.set(key, { ...course, ...existing });
            }
          });

        return Array.from(bestByKey.values());
      };

      const token = await getToken();
      const reqConfig = {
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      };

      const tryFetch = async (path) => {
        const res = await axios.get(api_endpoint + path, reqConfig);
        return normalizeCoursesPayload(res?.data);
      };

      const candidatePaths = token
        ? [
            "/course/tm-courses",
            "/course/managed",
            ...(isPrivilegedModeOn ? ["/course/admin"] : []),
            "/course/",
          ]
        : ["/course/"];

      const settled = await Promise.allSettled(
        candidatePaths.map((path) => tryFetch(path))
      );

      const lists = settled
        .filter((r) => r.status === "fulfilled")
        .map((r) => (Array.isArray(r.value) ? r.value : []));

      const merged = mergeCourseLists(lists);

      console.log("Courses fetched (merged):", merged);
      setCourses(Array.isArray(merged) ? merged : []);
    } catch (error) {
      console.error("Error fetching courses:", error);
      setCourses([]);
    }
  };

  useEffect(() => {
    if (!isLoaded) return;
    fetchCourses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded, isAdmin, isPrivilegedModeOn, getToken]);

  const normalizeCourseKey = (value) =>
    String(value || "")
      .trim()
      .toLowerCase()
      .replace(/[_-]+/g, " ")
      .replace(/[^\w\s]/g, " ")
      .replace(/\s+/g, " ");

  const getCourseSlugValue = useCallback((course) => {
    const candidates = [
      course?.slug,
      course?.course_slug,
      course?.courseSlug,
      course?.code,
      course?.course_code,
      course?.courseCode,
      course?.abbr,
      course?.course_abbr,
      course?.courseAbbr,
      course?.short_name,
      course?.course_short_name,
      course?.courseShortName,
    ];
    for (const candidate of candidates) {
      const value = String(candidate || "").trim();
      if (value) return value;
    }
    return "";
  }, []);

  const getCourseIdValue = useCallback((course) => {
    const value = course?.id ?? course?.course_id ?? course?.courseId;
    return value != null && `${value}`.trim() !== "" ? String(value) : "";
  }, []);

  const getCourseQualityScore = useCallback(
    (course) => {
      if (!course || typeof course !== "object") return 0;
      let score = 0;
      if (getCourseSlugValue(course)) score += 8;
      if (getCourseIdValue(course)) score += 4;
      const hasName = Boolean(
        normalizeCourseKey(
          course?.title ??
            course?.name ??
            course?.course_title ??
            course?.course_name ??
            course?.display_name ??
            ""
        )
      );
      if (hasName) score += 2;
      return score;
    },
    [getCourseIdValue, getCourseSlugValue]
  );

  const courseMetaByNameKey = useMemo(() => {
    const map = new Map();
    (Array.isArray(courses) ? courses : []).forEach((course) => {
      const keys = [
        course?.title,
        course?.name,
        course?.course_title,
        course?.courseTitle,
        course?.course_name,
        course?.courseName,
      ]
        .map((v) => normalizeCourseKey(v))
        .filter(Boolean);
      keys.forEach((key) => {
        const existing = map.get(key);
        if (!existing || getCourseQualityScore(course) >= getCourseQualityScore(existing)) {
          map.set(key, course);
        }
      });
    });
    return map;
  }, [courses, getCourseQualityScore]);

  const [topicNameById, setTopicNameById] = useState({});
  const [topicCourseIdById, setTopicCourseIdById] = useState({});
  const topicsLoadedCourseIdsRef = useRef(new Set());

  const courseById = useMemo(() => {
    const map = new Map();
    (Array.isArray(courses) ? courses : []).forEach((course) => {
      const id = getCourseIdValue(course);
      if (!id) return;
      const existing = map.get(String(id));
      if (!existing || getCourseQualityScore(course) >= getCourseQualityScore(existing)) {
        map.set(String(id), course);
      }
    });
    return map;
  }, [courses, getCourseIdValue, getCourseQualityScore]);

  const getProjectTopicId = useCallback((project) => {
    const sub = project?.submission;
    const proj = sub?.project;
    const value =
      proj?.topic_id ??
      proj?.topicId ??
      proj?.topic?.id ??
      proj?.topic?.topic_id ??
      proj?.topic?.topicId ??
      sub?.topic_id ??
      sub?.topicId ??
      sub?.topic?.id ??
      sub?.topic?.topic_id ??
      sub?.topic?.topicId ??
      project?.topic_id ??
      project?.topicId ??
      project?.topic?.id ??
      project?.topic?.topic_id ??
      project?.topic?.topicId ??
      null;
    return value != null && `${value}`.trim() !== "" ? String(value) : "";
  }, []);

  const getProjectCourseName = useCallback((project) => {
    const value =
      project?.course_name ??
      project?.courseName ??
      project?.course?.title ??
      project?.course?.name ??
      project?.course?.course_title ??
      project?.course?.course_name ??
      project?.submission?.course_name ??
      project?.submission?.courseName ??
      project?.submission?.course?.title ??
      project?.submission?.course?.name ??
      project?.submission?.course?.course_title ??
      project?.submission?.course?.course_name ??
      project?.submission?.project?.course_name ??
      project?.submission?.project?.courseName ??
      project?.submission?.project?.course?.title ??
      project?.submission?.project?.course?.name ??
      "";
    return String(value || "").trim();
  }, []);

  const extractDirectTopicTitle = useCallback((project) => {
    const candidates = [
      project?.topic_title,
      project?.topicTitle,
      project?.topic_name,
      project?.topicName,
      project?.topic?.title,
      project?.topic?.name,
      project?.submission?.topic_title,
      project?.submission?.topicTitle,
      project?.submission?.topic_name,
      project?.submission?.topicName,
      project?.submission?.topic?.title,
      project?.submission?.topic?.name,
      project?.submission?.project?.topic_title,
      project?.submission?.project?.topicTitle,
      project?.submission?.project?.topic_name,
      project?.submission?.project?.topicName,
      project?.submission?.project?.topic?.title,
      project?.submission?.project?.topic?.name,
      project?.submission?.project?.topic?.topic_title,
      project?.submission?.project?.topic?.topic_name,
    ];
    for (const candidate of candidates) {
      const text = String(candidate || "").replace(/_/g, " ").trim();
      if (text) return text;
    }
    return "";
  }, []);

  const topicNameByIdFromPayload = useMemo(() => {
    const map = {};
    const allProjects = [
      ...(Array.isArray(unreviewedProjects) ? unreviewedProjects : []),
      ...(Array.isArray(reviewedProjects) ? reviewedProjects : []),
    ];
    allProjects.forEach((project) => {
      const topicId = getProjectTopicId(project);
      if (!topicId || map[topicId]) return;
      const directTopicTitle = extractDirectTopicTitle(project);
      if (directTopicTitle) map[topicId] = directTopicTitle;
    });
    return map;
  }, [extractDirectTopicTitle, getProjectTopicId, reviewedProjects, unreviewedProjects]);

  useEffect(() => {
    if (!isLoaded || !user) return;

    const allProjects = [
      ...(Array.isArray(unreviewedProjects) ? unreviewedProjects : []),
      ...(Array.isArray(reviewedProjects) ? reviewedProjects : []),
    ];
    if (allProjects.length === 0) return;

    const neededTopicIds = new Set(
      allProjects
        .map((project) => getProjectTopicId(project))
        .filter((id) => id !== "")
    );

    // If we already resolved all needed topic names, skip.
    const missingTopicIds = Array.from(neededTopicIds).filter(
      (id) => !topicNameById?.[String(id)] && !topicNameByIdFromPayload?.[String(id)]
    );
    if (missingTopicIds.length === 0) return;

    let cancelled = false;

    (async () => {
      try {
        const token = await getToken();
        if (!token) return;

        const reqConfig = {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        };

        const remaining = new Set(missingTopicIds.map(String));
        const allCourses = Array.isArray(courses) ? courses : [];

        for (const course of allCourses) {
          if (cancelled) return;
          if (remaining.size === 0) return;

          const courseId = course?.id ?? course?.course_id ?? course?.courseId;
          if (courseId == null || `${courseId}`.trim() === "") continue;
          const courseIdStr = String(courseId);
          if (topicsLoadedCourseIdsRef.current.has(courseIdStr)) continue;

          topicsLoadedCourseIdsRef.current.add(courseIdStr);

          try {
            const topicResponse = await axios.get(
              api_endpoint + `/topic/${courseIdStr}`,
              reqConfig
            );

            const topics = topicResponse?.data?.topics ?? [];
            if (Array.isArray(topics) && topics.length > 0) {
              const nextNameEntries = [];
              const nextCourseEntries = [];
              topics.forEach((t) => {
                const id = t?.id ?? t?.topic_id ?? t?.topicId;
                if (id == null) return;
                const idStr = String(id);
                const rawName = t?.name ?? t?.title ?? "";
                const name = String(rawName).replace(/_/g, " ").trim();
                if (!name) return;

                nextNameEntries.push([idStr, name]);
                nextCourseEntries.push([idStr, courseIdStr]);
                if (remaining.has(idStr)) remaining.delete(idStr);
              });

              if (nextNameEntries.length > 0) {
                setTopicNameById((prev) => {
                  const next = { ...(prev || {}) };
                  nextNameEntries.forEach(([idStr, name]) => {
                    next[idStr] = name;
                  });
                  return next;
                });

                setTopicCourseIdById((prev) => {
                  const next = { ...(prev || {}) };
                  nextCourseEntries.forEach(([idStr, cId]) => {
                    next[idStr] = String(cId);
                  });
                  return next;
                });
              }
            }
          } catch (err) {
            // allow retry later
            topicsLoadedCourseIdsRef.current.delete(courseIdStr);
          }
        }
      } catch {
        // ignore
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [courses, getProjectTopicId, getToken, isLoaded, reviewedProjects, topicNameById, topicNameByIdFromPayload, unreviewedProjects, user]);

  const courseSlugMap = useMemo(() => {
    const map = {};
    const scoreByKey = {};
    courses.forEach((course) => {
      const slug = getCourseSlugValue(course);
      if (!slug) return;
      const score = getCourseQualityScore(course);
      [
        course?.title,
        course?.name,
        course?.course_title,
        course?.courseTitle,
        course?.course_name,
        course?.courseName,
        course?.display_name,
        course?.displayName,
      ]
        .map((v) => normalizeCourseKey(v))
        .filter(Boolean)
        .forEach((key) => {
          if (!(key in map) || score >= (scoreByKey[key] ?? -1)) {
            map[key] = slug;
            scoreByKey[key] = score;
          }
        });
    });
    return map;
  }, [courses, getCourseQualityScore, getCourseSlugValue]);

  const resolveCourseSlugForProject = useCallback((project, topicIdStr = "") => {
    const payloadSlugCandidates = [
      project?.course_slug,
      project?.courseSlug,
      project?.course_code,
      project?.courseCode,
      project?.course_abbr,
      project?.courseAbbr,
      project?.course_short_name,
      project?.courseShortName,
      project?.course?.slug,
      project?.course?.course_slug,
      project?.course?.courseSlug,
      project?.course?.code,
      project?.course?.course_code,
      project?.course?.courseCode,
      project?.course?.abbr,
      project?.course?.course_abbr,
      project?.course?.courseAbbr,
      project?.course?.short_name,
      project?.course?.course_short_name,
      project?.course?.courseShortName,
      project?.submission?.course_slug,
      project?.submission?.courseSlug,
      project?.submission?.course_code,
      project?.submission?.courseCode,
      project?.submission?.course?.slug,
      project?.submission?.course?.course_slug,
      project?.submission?.course?.courseSlug,
      project?.submission?.course?.code,
      project?.submission?.course?.course_code,
      project?.submission?.course?.courseCode,
      project?.submission?.course?.abbr,
      project?.submission?.course?.course_abbr,
      project?.submission?.course?.courseAbbr,
      project?.submission?.course?.short_name,
      project?.submission?.course?.course_short_name,
      project?.submission?.course?.courseShortName,
      project?.submission?.project?.course_slug,
      project?.submission?.project?.courseSlug,
      project?.submission?.project?.course_code,
      project?.submission?.project?.courseCode,
      project?.submission?.project?.course_abbr,
      project?.submission?.project?.courseAbbr,
      project?.submission?.project?.course_short_name,
      project?.submission?.project?.courseShortName,
      project?.submission?.project?.course?.slug,
      project?.submission?.project?.course?.course_slug,
      project?.submission?.project?.course?.courseSlug,
      project?.submission?.project?.course?.code,
      project?.submission?.project?.course?.abbr,
      project?.submission?.project?.course?.short_name,
    ];
    for (const candidate of payloadSlugCandidates) {
      const value = String(candidate || "").trim();
      if (value) return value;
    }

    const payloadCourseId =
      project?.course_id ??
      project?.courseId ??
      project?.course?.id ??
      project?.course?.course_id ??
      project?.course?.courseId ??
      project?.submission?.course_id ??
      project?.submission?.courseId ??
      project?.submission?.course?.id ??
      project?.submission?.course?.course_id ??
      project?.submission?.course?.courseId ??
      project?.submission?.project?.course_id ??
      project?.submission?.project?.courseId ??
      project?.submission?.project?.course?.id ??
      project?.submission?.project?.course?.course_id ??
      null;
    if (payloadCourseId != null && `${payloadCourseId}`.trim() !== "") {
      const c = courseById.get(String(payloadCourseId));
      const byIdSlug = getCourseSlugValue(c);
      if (byIdSlug) return byIdSlug;
    }

    const fromTopic = (() => {
      if (!topicIdStr) return "";
      const cId = topicCourseIdById?.[topicIdStr];
      if (!cId) return "";
      const c = courseById.get(String(cId));
      return getCourseSlugValue(c);
    })();
    if (fromTopic) return fromTopic;

    const courseNameCandidates = [
      getProjectCourseName(project),
      project?.course_name,
      project?.courseName,
      project?.course?.title,
      project?.course?.name,
      project?.course?.course_title,
      project?.course?.course_name,
      project?.submission?.course_name,
      project?.submission?.courseName,
      project?.submission?.course?.title,
      project?.submission?.course?.name,
      project?.submission?.course?.course_title,
      project?.submission?.course?.course_name,
      project?.submission?.project?.course_name,
      project?.submission?.project?.courseName,
      project?.submission?.project?.course?.title,
      project?.submission?.project?.course?.name,
      project?.submission?.project?.course?.course_title,
      project?.submission?.project?.course?.course_name,
    ]
      .map((value) => normalizeCourseKey(value))
      .filter(Boolean);

    for (const key of courseNameCandidates) {
      const courseMeta = courseMetaByNameKey.get(key);
      const fromMeta = getCourseSlugValue(courseMeta);
      if (fromMeta) return fromMeta;

      const fromMap = String(courseSlugMap[key] || "").trim();
      if (fromMap) return fromMap;
    }

    // Do not display the course title in the slug slot.
    return "";
  }, [courseById, courseMetaByNameKey, courseSlugMap, getCourseSlugValue, getProjectCourseName]);

  const resolveTopicTitleForProject = useCallback((project) => {
    const directTitle = extractDirectTopicTitle(project);
    if (directTitle) return directTitle;

    const topicIdStr = getProjectTopicId(project);
    if (!topicIdStr) return "";

    const fromPayloadMap = String(topicNameByIdFromPayload?.[topicIdStr] || "").trim();
    if (fromPayloadMap) return fromPayloadMap;

    const fromTopicsApi = String(topicNameById?.[topicIdStr] || "").replace(/_/g, " ").trim();
    if (fromTopicsApi) return fromTopicsApi;

    return "";
  }, [extractDirectTopicTitle, getProjectTopicId, topicNameById, topicNameByIdFromPayload]);

  useEffect(() => {
    if (!import.meta.env.DEV) return;

    const allProjects = [
      ...(Array.isArray(unreviewedProjects) ? unreviewedProjects : []),
      ...(Array.isArray(reviewedProjects) ? reviewedProjects : []),
    ];
    if (allProjects.length === 0) return;

    const unresolved = allProjects
      .map((project) => {
        const topicId = getProjectTopicId(project);
        const courseName = getProjectCourseName(project);
        const topicTitle = resolveTopicTitleForProject(project);
        const courseSlug = resolveCourseSlugForProject(project, topicId);
        return {
          projectId:
            project?.submission?.project_id ??
            project?.submission?.project?.id ??
            project?.id ??
            null,
          topicId: topicId || null,
          courseName: courseName || null,
          courseSlug: courseSlug || null,
          topicTitle: topicTitle || null,
        };
      })
      .filter((item) => !item.courseSlug || !item.topicTitle);

    if (unresolved.length > 0) {
      console.warn("[ReviewProjects] Unresolved course/topic metadata", {
        total: unresolved.length,
        samples: unresolved.slice(0, 20),
      });
    }
  }, [
    getProjectCourseName,
    getProjectTopicId,
    resolveCourseSlugForProject,
    resolveTopicTitleForProject,
    reviewedProjects,
    unreviewedProjects,
  ]);

  const refetchReviewedProjects = (assignment = null) => {
    // Optimistic UI update (especially important in instructor/non-privileged mode
    // where reassignment should remove the item from the list immediately).
    const resolvedAssignment = assignment && typeof assignment === "object" ? assignment : null;
    const assignedStudentId = String(resolvedAssignment?.submissionUserId ?? "").trim();
    const assignedProjectId = String(resolvedAssignment?.submissionProjectId ?? "").trim();
    const assignedReviewerId = String(resolvedAssignment?.reviewerId ?? "").trim();

    const effectiveUserId = String(userId || user?.id || "").trim();
    const hasIds = Boolean(assignedStudentId && assignedProjectId);

    if (hasIds) {
      setUnreviewedProjects((prev) => {
        const items = Array.isArray(prev) ? prev : [];

        // In non-privileged mode, the list is typically scoped to "my assigned" projects.
        // If I reassign away from myself, remove it immediately.
        if (!isPrivilegedModeOn && effectiveUserId && assignedReviewerId && assignedReviewerId !== effectiveUserId) {
          return items.filter((p) => {
            const submission = p?.submission || null;
            const studentId =
              submission?.user_id ??
              submission?.userId ??
              submission?.student_id ??
              submission?.studentId ??
              null;
            const projectId =
              submission?.project_id ??
              submission?.projectId ??
              submission?.project?.id ??
              submission?.project?._id ??
              null;

            return !(
              String(studentId ?? "").trim() === assignedStudentId &&
              String(projectId ?? "").trim() === assignedProjectId
            );
          });
        }

        // Otherwise, patch the assigned reviewer id so the label updates.
        return items.map((p) => {
          const submission = p?.submission || null;
          const studentId =
            submission?.user_id ??
            submission?.userId ??
            submission?.student_id ??
            submission?.studentId ??
            null;
          const projectId =
            submission?.project_id ??
            submission?.projectId ??
            submission?.project?.id ??
            submission?.project?._id ??
            null;

          const matches =
            String(studentId ?? "").trim() === assignedStudentId &&
            String(projectId ?? "").trim() === assignedProjectId;

          if (!matches) return p;

          return {
            ...p,
            submission: {
              ...(submission || {}),
              assigned_reviewer: assignedReviewerId || submission?.assigned_reviewer,
              assignedReviewer: assignedReviewerId || submission?.assignedReviewer,
            },
          };
        });
      });
    }

    // After mutations (review, reassignment), always refetch even if the current
    // page key was recently fetched. Use background mode to avoid full-page spinners.
    fetchReviewed({ background: true });
    fetchUnreviewed({ background: true });

    // Some backends are eventually consistent for assignment writes; do a short delayed
    // refetch to ensure the list reflects the update.
    if (hasIds && typeof window !== "undefined") {
      if (postAssignRefetchTimerRef.current) {
        clearTimeout(postAssignRefetchTimerRef.current);
        postAssignRefetchTimerRef.current = null;
      }
      postAssignRefetchTimerRef.current = setTimeout(() => {
        fetchReviewed({ background: true });
        fetchUnreviewed({ background: true });
      }, 900);
    }
  };

  //    Filter and Sort Configurations
  const courseOptions = useMemo(() => {
    return [
      { value: "all", label: "All Courses" },
      ...courses.map((course) => ({
        value: course.title,
        label: course.title,
      })),
    ];
  }, [courses]);

  const projectTypeOptions = useMemo(() => {
    const allTypes = [...unreviewedProjects, ...reviewedProjects]
      .map(
        (project) => project.project?.type || project.submission?.project?.type
      )
      .filter(Boolean);

    const uniqueTypes = [...new Set(allTypes)];

    return [
      { value: "all", label: "All Types" },
      ...uniqueTypes.map((type) => ({
        value: type,
        label: type.charAt(0).toUpperCase() + type.slice(1),
      })),
    ];
  }, [unreviewedProjects, reviewedProjects]);

  const resolveInstructorLabel = (project) => {
    const id = getInstructorIdFromProject(project);
    if (id) {
      const u = instructorById.get(String(id));
      if (u) {
        const name = `${u?.first_name ?? ""} ${u?.last_name ?? ""}`.trim();
        return name || u?.email || id;
      }
    }

    const reviewerObj =
      project?.reviewer ??
      project?.reviews?.reviewer ??
      project?.reviews?.tm_reviews?.[0]?.reviewer ??
      project?.tm_reviews?.[0]?.reviewer ??
      null;
    if (reviewerObj && typeof reviewerObj === "object") {
      const name = `${reviewerObj?.first_name ?? ""} ${reviewerObj?.last_name ?? ""}`.trim();
      return name || reviewerObj?.email || id || "Unknown Instructor";
    }

    const first =
      project?.tm_first_name ??
      project?.tmFirstName ??
      project?.owner_first_name ??
      project?.ownerFirstName ??
      project?.graded_by_first_name ??
      project?.gradedByFirstName ??
      project?.reviewer_first_name ??
      project?.reviewerFirstName ??
      "";
    const last =
      project?.tm_last_name ??
      project?.tmLastName ??
      project?.owner_last_name ??
      project?.ownerLastName ??
      project?.graded_by_last_name ??
      project?.gradedByLastName ??
      project?.reviewer_last_name ??
      project?.reviewerLastName ??
      "";
    const name = `${first} ${last}`.trim();
    if (name) return name;

    return id || "Unknown Instructor";
  };

  const instructorFilterOptions = useMemo(() => {
    if (!isPrivilegedModeOn) return [];

    const allProjects = [...(unreviewedProjects || []), ...(reviewedProjects || [])];
    const idsFromData = new Set(
      allProjects
        .map(getInstructorIdFromProject)
        .filter((v) => String(v || "").trim() !== "")
        .map((v) => String(v))
    );

    // Primary list: all instructors from /user/users/role/Instructor
    const baseOptions = (Array.isArray(instructors) ? instructors : [])
      .map((u) => {
        const value =
          String(u?.clerk_id ?? u?.clerkId ?? u?.user_id ?? u?.userId ?? u?.id ?? "").trim();
        if (!value) return null;
        const label = `${u?.first_name ?? ""} ${u?.last_name ?? ""}`.trim() || u?.email || value;
        return { value, label };
      })
      .filter(Boolean);

    const existing = new Set(baseOptions.map((o) => String(o.value)));
    const extraFromData = Array.from(idsFromData)
      .filter((id) => !existing.has(String(id)))
      .map((id) => ({ value: String(id), label: String(id) }));

    const options = [...baseOptions, ...extraFromData];
    options.sort((a, b) => String(a.label).localeCompare(String(b.label)));
    return [{ value: "ALL", label: "All Instructors" }, ...options];
  }, [getInstructorIdFromProject, instructorById, instructors, isPrivilegedModeOn, reviewedProjects, unreviewedProjects]);
  const filterConfig = [
    {
      label: "Filter by Course",
      value: filterBy,
      onChange: setFilterBy,
      onClear: () => setFilterBy("all"),
      options: courseOptions,
    },
    {
      label: "Filter by Type",
      value: projectTypeFilter,
      onChange: setProjectTypeFilter,
      onClear: () => setProjectTypeFilter("all"),
      options: projectTypeOptions,
    },
  ];

  const sortConfig = [
    {
      label: "Sort by",
      value: sortBy,
      onChange: setSortBy,
      onClear: () => setSortBy("submissionDate"),
      options: [
        { value: "submissionDate", label: "Submission Date" },
        { value: "title", label: "Title" },
        { value: "category", label: "Category" },
      ],
    },
  ];

  const filterAndSortProjects = (projects) => {
    let filtered = Array.isArray(projects) ? [...projects] : [];

    const normalize = (value) => String(value ?? "").toLowerCase().trim();
    const normalizeLabel = (value) =>
      String(value ?? "")
        .toLowerCase()
        .replace(/\s+/g, " ")
        .trim();
    const needle = normalize(searchTerm);

    const getSubmitterFirst = (project) =>
      (
        project?.user_first_name ??
        project?.userFirstName ??
        project?.student_first_name ??
        project?.studentFirstName ??
        project?.submission?.user_first_name ??
        project?.submission?.userFirstName ??
        project?.submission?.student_first_name ??
        project?.submission?.studentFirstName ??
        ""
      );

    const getSubmitterLast = (project) =>
      (
        project?.user_last_name ??
        project?.userLastName ??
        project?.student_last_name ??
        project?.studentLastName ??
        project?.submission?.user_last_name ??
        project?.submission?.userLastName ??
        project?.submission?.student_last_name ??
        project?.submission?.studentLastName ??
        ""
      );

    const getReviewerFirst = (project) =>
      (
        project?.reviewer_first_name ??
        project?.reviewerFirstName ??
        project?.graded_by_first_name ??
        project?.gradedByFirstName ??
        project?.tm_first_name ??
        project?.tmFirstName ??
        ""
      );

    const getReviewerLast = (project) =>
      (
        project?.reviewer_last_name ??
        project?.reviewerLastName ??
        project?.graded_by_last_name ??
        project?.gradedByLastName ??
        project?.tm_last_name ??
        project?.tmLastName ??
        ""
      );

    const getProjectTitle = (project) =>
      (
        project?.submission?.project?.title ??
        project?.submission?.project?.name ??
        project?.project?.title ??
        project?.project?.name ??
        project?.submission?.title ??
        project?.title ??
        ""
      );

    const getCourseSlugForSearch = (project) => {
      const direct =
        project?.course_slug ??
        project?.courseSlug ??
        project?.submission?.course_slug ??
        project?.submission?.courseSlug ??
        project?.submission?.project?.course_slug ??
        project?.submission?.project?.courseSlug ??
        "";
      if (String(direct || "").trim()) return String(direct);
      try {
        const topicId = getProjectTopicId(project);
        return resolveCourseSlugForProject(project, topicId);
      } catch {
        return "";
      }
    };

    // Filter by search term
    if (needle) {
      filtered = filtered.filter((project) => {
        const submitterFirst = String(getSubmitterFirst(project) || "").trim();
        const submitterLast = String(getSubmitterLast(project) || "").trim();
        const submitterFull = `${submitterFirst} ${submitterLast}`.trim();

        const reviewerFirst = String(getReviewerFirst(project) || "").trim();
        const reviewerLast = String(getReviewerLast(project) || "").trim();
        const reviewerFull = `${reviewerFirst} ${reviewerLast}`.trim();

        const reviewerLabel = String(resolveInstructorLabel(project) || "").trim();
        const title = String(getProjectTitle(project) || "").trim();
        const courseSlug = String(getCourseSlugForSearch(project) || "").trim();

        const haystack = [
          title,
          submitterFirst,
          submitterLast,
          submitterFull,
          reviewerFirst,
          reviewerLast,
          reviewerFull,
          reviewerLabel,
          courseSlug,
        ]
          .filter(Boolean)
          .map(normalize)
          .join("\n");

        return haystack.includes(needle);
      });
    }

    // Filter by course
    if (filterBy && filterBy !== "all") {
      filtered = filtered.filter((project) => project.course_name === filterBy);
    }

    // Filter by project type
    if (projectTypeFilter && projectTypeFilter !== "all") {
      filtered = filtered.filter(
        (project) =>
          (project.project?.type || project.submission?.project?.type) ===
          projectTypeFilter
      );
    }

    // Filter by Instructor (privileged-mode only)
    if (isPrivilegedModeOn && instructorFilter !== "ALL") {
      const wanted = String(instructorFilter);
      const wantedUser = instructorById.get(String(wanted));
      const wantedLabel = wantedUser
        ? `${wantedUser?.first_name ?? ""} ${wantedUser?.last_name ?? ""}`.trim() || wantedUser?.email || wanted
        : wanted;
      const wantedNorm = normalizeLabel(wantedLabel);

      filtered = filtered.filter((project) => {
        const id = String(getInstructorIdFromProject(project) || "");
        if (id && id === wanted) return true;
        if (!wantedNorm) return false;
        return normalizeLabel(resolveInstructorLabel(project)) === wantedNorm;
      });
    }

    // Sort projects
    filtered.sort((a, b) => {
      switch (sortBy) {
        case "title":
          return (
            a.project?.title ||
            a.submission?.project?.title ||
            ""
          ).localeCompare(
            b.project?.title || b.submission?.project?.title || ""
          );
        case "category":
          return (
            a.project?.type ||
            a.submission?.project?.type ||
            ""
          ).localeCompare(b.project?.type || b.submission?.project?.type || "");
        case "submissionDate":
        default:
          return (
            new Date(b.submission?.submitted_on) -
            new Date(a.submission?.submitted_on)
          );
      }
    });

    return filtered;
  };

  // Calculate filtered projects for each tab
  const filteredUnreviewedProjects = useMemo(() => {
    return filterAndSortProjects(unreviewedProjects);
  }, [unreviewedProjects, searchTerm, filterBy, projectTypeFilter, sortBy, isPrivilegedModeOn, instructorFilter]);

  const filteredReviewedProjects = useMemo(() => {
    return filterAndSortProjects(reviewedProjects);
  }, [reviewedProjects, searchTerm, filterBy, projectTypeFilter, sortBy, isPrivilegedModeOn, instructorFilter]);

  const unreviewedTotalPages = Math.max(1, Number(unreviewedPagination?.total_pages ?? 1) || 1);
  const reviewedTotalPages = Math.max(1, Number(reviewedPagination?.total_pages ?? 1) || 1);
  const unreviewedTotalRows = Number(unreviewedPagination?.total_count ?? 0) || 0;
  const reviewedTotalRows = Number(reviewedPagination?.total_count ?? 0) || 0;

  return (
    <div className="flex flex-col w-full min-h-screen">
      <div className="max-w-7xl w-full mx-auto">
        <h1>{sidebarTitle || "Review Assignments"}</h1>
        <p className="text-(--neutral-800)">
          Review and manage project submissions efficiently, track progress, and
          provide feedback to learners.
        </p>

        <Tabs
          value={activeTab}
          onValueChange={setActiveTab}
          className="w-full space-y-4 mt-3"
        >
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <TabsList className="w-full md:w-auto">
              <TabsTrigger value="unreviewed" className="w-full md:w-auto">
                Unreviewed
              </TabsTrigger>
              <TabsTrigger value="reviewed" className="w-full md:w-auto">
                Reviewed
              </TabsTrigger>
            </TabsList>
          </div>

          {/* Standardized TableToolbar */}
          <TableToolbar
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            filters={[
              ...(isPrivilegedModeOn
                ? [
                    {
                      label: "Instructor",
                      value: pendingInstructorFilter,
                      onChange: setPendingInstructorFilter,
                      onClear: () => setPendingInstructorFilter("ALL"),
                      options: instructorFilterOptions,
                    },
                  ]
                : []),
              {
                label: "Filter by Course",
                value: pendingFilterBy,
                onChange: setPendingFilterBy,
                onClear: () => setPendingFilterBy("all"),
                options: courseOptions,
              },
              {
                label: "Filter by Type",
                value: pendingProjectTypeFilter,
                onChange: setPendingProjectTypeFilter,
                onClear: () => setPendingProjectTypeFilter("all"),
                options: projectTypeOptions,
              },
            ]}
            sorts={[
              {
                label: "Sort by",
                value: pendingSortBy,
                onChange: setPendingSortBy,
                onClear: () => setPendingSortBy("submissionDate"),
                options: [
                  { value: "submissionDate", label: "Submission Date" },
                  { value: "title", label: "Title" },
                  { value: "category", label: "Category" },
                ],
              },
            ]}
            onApplyFilters={() => {
              setFilterBy(pendingFilterBy);
              setProjectTypeFilter(pendingProjectTypeFilter);
              if (isPrivilegedModeOn) setInstructorFilter(pendingInstructorFilter);
            }}
            onApplySort={() => {
              setSortBy(pendingSortBy);
            }}
          />

          {/* Tab Content */}
          <TabsContent value="unreviewed" forceMount>
            {shouldRenderUnreviewedTab ? (
              unreviewedLoading ? (
                <UnreviewedCardsSkeleton rows={6} />
              ) : unreviewedProjects.length === 0 ? (
                <EmptyState
                  icon={BookOpenIcon}
                  title="No unreviewed projects"
                  description="There are no unreviewed project submissions at the moment."
                />
              ) : filteredUnreviewedProjects.length === 0 ? (
                <EmptyState
                  icon={BookOpenIcon}
                  title="Project not found"
                  description="No projects match your filters or search."
                />
              ) : (
                <>
                  <div className="space-y-4">
                    {filteredUnreviewedProjects.map((project) => {
                      const submission = project?.submission;
                      if (!submission) return null;

                      const submissionId =
                        project?.submission_id ??
                        project?.submissionId ??
                        submission?.submission_id ??
                        submission?.submissionId ??
                        submission?.id ??
                        submission?._id ??
                        "";

                      const key = `${submission?.project_id ?? "p"}-${submission?.user_id ?? "u"}-${submission?.submitted_on ?? submission?.last_updated_at ?? "t"}`;

                      const reviewerFirstName =
                        project?.reviewer_first_name ??
                        project?.reviewerFirstName ??
                        project?.tm_first_name ??
                        project?.tmFirstName ??
                        (() => {
                          const id =
                            submission?.assigned_reviewer ??
                            submission?.assignedReviewer ??
                            project?.submission?.assigned_reviewer ??
                            project?.submission?.assignedReviewer ??
                            null;
                          if (id == null) return "";
                          const u = instructorById.get(String(id));
                          return u?.first_name ?? u?.firstName ?? "";
                        })();
                      const reviewerLastName =
                        project?.reviewer_last_name ??
                        project?.reviewerLastName ??
                        project?.tm_last_name ??
                        project?.tmLastName ??
                        (() => {
                          const id =
                            submission?.assigned_reviewer ??
                            submission?.assignedReviewer ??
                            project?.submission?.assigned_reviewer ??
                            project?.submission?.assignedReviewer ??
                            null;
                          if (id == null) return "";
                          const u = instructorById.get(String(id));
                          return u?.last_name ?? u?.lastName ?? "";
                        })();

                      const topicIdStr = getProjectTopicId(project);
                      const topicTitle = resolveTopicTitleForProject(project);
                      const courseSlug = resolveCourseSlugForProject(project, topicIdStr);
                      const courseName = getProjectCourseName(project);

                      return (
                        <UnreviewedCard
                          key={key}
                          submission={submission}
                          submissionId={submissionId}
                          courseName={courseName}
                          courseSlug={courseSlug}
                          userFirstName={project.user_first_name}
                          userLastName={project.user_last_name}
                          userEmail={project.user_email}
                          reviewerFirstName={reviewerFirstName}
                          reviewerLastName={reviewerLastName}
                          viewerIsReviewer={!isPrivilegedModeOn}
                          topicTitle={topicTitle}
                          refetchReviewedProjects={refetchReviewedProjects}
                        />
                      );
                    })}
                  </div>

                  {unreviewedHydratingRest ? (
                    <div className="mt-6">
                      <UnreviewedCardsSkeleton rows={3} />
                    </div>
                  ) : null}

                  <Pagination
                    currentPage={unreviewedPage}
                    totalPages={unreviewedTotalPages}
                    rowsPerPage={unreviewedRowsPerPage}
                    rowsPerPageOptions={[10, 20, 50]}
                    onRowsPerPageChange={(value) => {
                      setUnreviewedRowsPerPage(value);
                      setUnreviewedPage(1);
                    }}
                    onPageChange={(page) =>
                      setUnreviewedPage(Math.max(1, Math.min(page, unreviewedTotalPages)))
                    }
                    totalRows={unreviewedTotalRows}
                    rowLabel="projects"
                    showSelectedCount={false}
                  />
                </>
              )
            ) : null}
          </TabsContent>

          <TabsContent value="reviewed" forceMount>
            {shouldRenderReviewedTab ? (
              reviewedLoading ? (
                <ReviewedCardsSkeleton rows={6} />
              ) : reviewedProjects.length === 0 ? (
                <EmptyState
                  icon={BookOpenIcon}
                  title="No reviewed projects"
                  description="There are no reviewed project submissions yet."
                />
              ) : filteredReviewedProjects.length === 0 ? (
                <EmptyState
                  icon={BookOpenIcon}
                  title="Project not found"
                  description="No projects match your filters or search."
                />
              ) : (
                <>
                  <div className="space-y-6">
                    {filteredReviewedProjects.map((project) => {
                      const topicIdStr = getProjectTopicId(project);
                      const topicTitle = resolveTopicTitleForProject(project);
                      const courseSlug = resolveCourseSlugForProject(project, topicIdStr);
                      const courseName = getProjectCourseName(project);

                      return (
                        <ReviewedCard
                          key={`${project.submission?.project_id ?? "p"}-${project.submission?.user_id ?? "u"}-${project.submission?.submitted_on ?? project.submission?.last_updated_at ?? "t"}`}
                          submission={project.submission}
                          initialTmReview={project?.tmReview ?? null}
                          courseName={courseName}
                          courseSlug={courseSlug}
                          topicTitle={topicTitle}
                          userFirstName={project.user_first_name}
                          userLastName={project.user_last_name}
                          userEmail={project.user_email}
                          gradedByFirstName={project.graded_by_first_name}
                          gradedByLastName={project.graded_by_last_name}
                          gradedByEmail={project.graded_by_email}
                          onReviewUpdated={refetchReviewedProjects}
                        />
                      );
                    })}
                  </div>

                  {reviewedHydratingRest ? (
                    <div className="mt-6">
                      <ReviewedCardsSkeleton rows={3} />
                    </div>
                  ) : null}

                  <Pagination
                    currentPage={reviewedPage}
                    totalPages={reviewedTotalPages}
                    rowsPerPage={reviewedRowsPerPage}
                    rowsPerPageOptions={[10, 20, 50]}
                    onRowsPerPageChange={(value) => {
                      setReviewedRowsPerPage(value);
                      setReviewedPage(1);
                    }}
                    onPageChange={(page) =>
                      setReviewedPage(Math.max(1, Math.min(page, reviewedTotalPages)))
                    }
                    totalRows={reviewedTotalRows}
                    rowLabel="projects"
                    showSelectedCount={false}
                  />
                </>
              )
            ) : null}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
